import { ReactNode } from 'react';
import { MemberView } from './adapters';
import { MemberStage, STAGE_META } from './tabData';
import OpsDetailModal from './OpsDetailModal';

/**
 * 회원 상세 — 화면 한가운데 뜨는 팝업.
 *
 * 브리핑 상세(BriefingDetailDrawer)·강사 데이터(CoachDataDrawer)와 같은 관용구입니다 —
 * 어두운 막 + 가운데 팝업 + 네이비 머리말. 껍데기는 세 화면이 `OpsDetailModal`
 * 하나를 같이 씁니다. 화면마다 다르게 열리면 원장님은 조작을 여러 벌 배워야 합니다.
 *
 * (예전에는 오른쪽에서 밀려 나오는 서랍이었습니다. 회원 상세는 내용이 짧아
 *  서랍 아래가 늘 텅 비었습니다 — 그 이야기는 OpsDetailModal 첫머리에 있습니다.)
 *
 * ⚠️ 이 파일은 **머리말만** 맡습니다. 안에 들어가는 내용(근거·진단·지시 버튼)은
 *    목록 화면이 원래 쓰던 상세 카드를 그대로 children 으로 넣습니다.
 *    상세를 여기서 다시 그리면 목록을 열었을 때와 안 열었을 때 화면이 갈립니다.
 */

type Props = {
    member: MemberView;
    /** 지금 보고 있는 단계 — 머리말 배지와 카드 테두리가 여기서 나온다 */
    stage: MemberStage;
    onClose: () => void;
    /**
     * Esc 를 이 팝업이 먹으면 안 되는 순간.
     *
     * 지시 확인 창이 이 위에 떠 있을 때 Esc 를 누르면 **확인 창만** 닫혀야 합니다.
     * 둘 다 닫히면 원장님은 취소를 눌렀을 뿐인데 보던 회원까지 잃습니다.
     */
    escapeLocked?: boolean;
    children: ReactNode;
};

const MemberDetailDrawer = ({ member, stage, onClose, escapeLocked = false, children }: Props) => {
    const meta = STAGE_META[stage];

    return (
        <OpsDetailModal
            label={`${member.name} 회원 상세`}
            closeLabel="상세 닫기"
            width={720}
            onClose={onClose}
            escapeLocked={escapeLocked}
            header={
                <div className="flex items-center" style={{ gap: 14 }}>
                    <div
                        className="flex items-center justify-center rounded-full"
                        style={{
                            width: 48,
                            height: 48,
                            background: 'rgba(255,255,255,0.16)',
                            color: '#FFFFFF',
                            fontSize: 19,
                            fontWeight: 800,
                            flex: 'none',
                        }}
                    >
                        {member.name.slice(0, 1)}
                    </div>
                    <div className="flex flex-col">
                        <div className="flex items-center" style={{ gap: 8 }}>
                            <span
                                style={{
                                    fontSize: 20,
                                    fontWeight: 800,
                                    color: '#FFFFFF',
                                    letterSpacing: '-0.02em',
                                }}
                            >
                                {member.name} 회원
                            </span>
                            <span
                                className="rounded-[5px]"
                                style={{
                                    background: 'rgba(255,255,255,0.16)',
                                    color: '#FFFFFF',
                                    fontSize: 11.5,
                                    fontWeight: 800,
                                    padding: '3px 8px',
                                }}
                            >
                                {meta.label}
                            </span>
                        </div>
                        <span style={{ fontSize: 13, color: '#9DB4D8', marginTop: 4 }}>
                            {/* 담당 강사가 없는 회원은 없는 대로 말한다 — 빈칸을 남기지 않는다 */}
                            {member.coachId ? `${member.coachName} 강사` : '담당 강사 없음'} ·{' '}
                            {member.product} · {member.passOrder.label} 수강권
                        </span>
                    </div>
                </div>
            }
        >
            {/* 본문 — 목록이 쓰던 상세 카드 그대로 */}
            <div
                className="w-full overflow-hidden rounded-[14px] border border-solid"
                style={{ borderColor: meta.border, background: '#FFFFFF' }}
            >
                {children}
            </div>
        </OpsDetailModal>
    );
};

export default MemberDetailDrawer;
